import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { MouseEvent, ReactNode } from 'react'
import TagManager from 'react-gtm-module'

import AllAccountsModal from './components/AllAccountsModal'
import ApplicationDropdown from './components/navactions/product/ApplicationDropdown'

import { Application, APPLICATION_SUITE_MAPPING, AppSuite, Suite, URLBuilders } from './suite/common'

import * as AuthRoutingSuite from './suite/AuthRoutingSuite'
import * as GlobalSettingsSuite from './suite/GlobalSettingsSuite'
import * as Q4mSuite from './suite/Q4mSuite'

import HELP_TEXT from './constants/helpText'
import { DataService } from './dataService'
import { Account, Organization, User } from './models'
import { getAccountIdFromLocalStorage, getAllAccountFromLocalStorage, setOrgIdToLocalStorage } from './utils/helpers'

import AccountService, { Application as AccountServiceApplication } from '@qc/account-service'
import ActionContainer from './components/navactions/ActionContainer'
import styles from './globalnav.scss'
import { InjectionConfig, WINDOW_REF } from './InjectionConfig'
import GlobalNavPopupContext, { GlobalNavPopup, PopupType } from './PopupContext'

import Button from '@qc/particle/lib/components/button'
import Spin from '@qc/particle/lib/components/spin'

interface GlobalNavProps {
  accountInfo?: Partial<Account>
  currentApp: Application
  onUpdate?: (context: GlobalNavContextInfo) => void
}

export interface State {
  context: GlobalNavContextInfo
  popUpContext: GlobalNavPopup
  accounts: Account[]
  appSuite: Suite
  helpText: string
}

interface GlobalNavContextInfo {
  account: Account | null
  accounts: Account[]
  organizations: Organization[]
  user: User | null
  isLoading: boolean
  userInitError?: string
  accountsInitError?: string
  onAccountChanged: (Account: Account) => void
  enabledApps: string[]
}

type AccountSniffer = Partial<{ [k in keyof Account]: string }>
interface AccountSniffingResult {
  account: Account | null
  fromURL: boolean
}
const ACCOUNT_SNIFFER: AccountSniffer = {
  id: '([^/]+)',
  pCode: '([^/]+)',
}

const DEFAULT_CONTEXT_STATE: GlobalNavContextInfo = {
  account: null,
  accounts: [],
  organizations: [],
  user: null,
  isLoading: false,
  onAccountChanged: () => undefined,
  enabledApps: [],
}

const GlobalNavContext = React.createContext<GlobalNavContextInfo>(DEFAULT_CONTEXT_STATE)

TagManager.initialize({
  gtmId: 'GTM-5JPC9W'
})

class GlobalNav extends React.PureComponent<GlobalNavProps, State> {
  private readonly dataService: DataService
  private readonly window: Window
  private readonly accountServiceAPI: AccountService = new AccountService()

  constructor (props: GlobalNavProps) {
    super(props)
    this.state = {
      context: {
        user: null,
        account: null,
        accounts: [],
        organizations: [],
        isLoading: true,
        onAccountChanged: this.handleAccountSelect,
        enabledApps: [],
      },
      popUpContext: {
        openPopup: null,
        setOpenPopup: this.setOpenPopup
      },
      accounts: [],
      appSuite: this.getAppSuite(props.currentApp),
      helpText: HELP_TEXT.DEFAULT,
    }
    this.dataService = InjectionConfig.get(DataService)
    this.window = InjectionConfig.get(WINDOW_REF) || window
  }

  public async componentDidMount () {
    const user = await this.loadUserInformation()
    const orgIdFromUrl = window.location.pathname.split('/')[3]
    const orgId = parseInt(orgIdFromUrl, 10)

    if (!isNaN(orgId)) {
      setOrgIdToLocalStorage(orgId)
    }

    if (user) {
      this.setState({ context: { ...this.state.context, user } })

      const { accounts, organizations } = await this.loadAccountsInformation(this.getAccountInfo())
      const accountSniffingResult = this.getSelectedAccount(accounts)
      this.setState({
        context: {
          user,
          account: accountSniffingResult.account,
          accounts,
          organizations,
          isLoading: false,
          onAccountChanged: this.handleAccountSelect,
          enabledApps: [],
        },
        accounts: this.getAccountsWithLastAccessed(accounts)
      })
    }
  }

  componentDidUpdate (prevProps: GlobalNavProps, prevState: State) {
    if (this.props.onUpdate) {
      const prevContext = prevState.context
      if (!isEqual(prevContext, this.state.context)) {
        this.props.onUpdate(this.state.context)
      }
      if (!isEqual(prevContext.account, this.state.context.account)) {
        this.handleAppAccess(this.state.context.account)
      }
    }
  }

  toggleDropdown = () => {
    this.setOpenPopup(PopupType.PRODUCT)
  }

  handleLogout = (event) => {
    event.preventDefault()
    fetch('/user/removeSession', { method: 'POST' }).then(() => window.location.reload())
  }

  public render (): ReactNode {
    const { appSuite,
      context: { account, user, enabledApps },
      accounts,
      popUpContext: { openPopup }
    } = this.state
    const { currentApp } = this.props

    let actionSection
    if (!user) {
      actionSection = (
        <div className={styles.login_actions}>
          <Button size="large" type="primary" href={`/signup?forward=${this.window.location.pathname}`}>SIGN UP</Button>
          <Button size="large" type="text" href={this.buildLoginUrl()}>LOG IN</Button>
        </div>
      )
    } else {
      actionSection =
        (
          <ActionContainer
            notificationsVisible={appSuite.SHOW_NOTIFICATION_DROPDOWN}
            dataservice={this.dataService}
            currentApp={currentApp}
            onLogout={this.handleLogout}
            showOrgList={appSuite.SHOW_ORG_SWITCHER}
            showAccountList={appSuite.SHOW_ACCOUNT_SWITCHER}
            showUserInfo={appSuite.SHOW_USER_INFO}
            showAppsDropdown={appSuite.SHOW_APPS_DROPDOWN}
          />
        )
    }
    return (
      <GlobalNavContext.Provider value={this.state.context}>
        <GlobalNavPopupContext.Provider value={this.state.popUpContext}>
          <div className={styles.gn_wrapper + ' gn_particle_base'}>
            <div
              className={`gn_root`}
            >
              {openPopup === PopupType.ACCOUNTS_MODAL &&
                <AllAccountsModal
                  currentAcc={account}
                  accountSelectHandler={this.handleAccountSelect}
                  onClose={this.toggleShowAllAccountsModal}
                />
              }

              <nav className="globalNav">
                {this.state.appSuite.INCLUDE_BANNER_LINK ? (
                  <a className="nav__product" href={this.buildURL(Application.AdsManager, this.state.context.account)}>
                    <span data-cy="productLogo" className={`nav__product__logo_${appSuite.SUITE_NAME}`} />
                  </a>
                ) : (
                  <span data-cy="productLogo" className={`nav__product__logo_${appSuite.SUITE_NAME}`} />
                )}
                <span className="nav__product__separator" />
                <div className="nav__product__selector__container">
                  {appSuite.SHOW_PRODUCT_DROPDOWN && (
                    <ApplicationDropdown
                      account={account}
                      currentApp={currentApp}
                      appSuite={appSuite}
                      buildUrl={this.buildURL}
                      enabledApps={enabledApps}
                    />
                  )}
                </div>
                {!this.state.context.isLoading && actionSection}
                {this.state.context.isLoading && (<Spin size="small" />)}
              </nav>
              {this.props.children}
            </div>
          </div>
        </GlobalNavPopupContext.Provider>
      </GlobalNavContext.Provider >
    )
  }

  private getAppSuite = (app: Application): Suite => {
    if (APPLICATION_SUITE_MAPPING[app] === AppSuite.GlobalSettingsSuite) {
      return GlobalSettingsSuite
    }

    if (APPLICATION_SUITE_MAPPING[app] === AppSuite.AuthRoutingSuite) {
      return AuthRoutingSuite
    }

    return Q4mSuite
  }

  private buildLoginUrl = (): string => {
    const path = this.window.location.pathname
    const hash = this.window.location.hash
    return `/user/login?qcRefer=${path}${hash ? `#${hash}` : ''}`
  }

  private ignoreClick = (event: MouseEvent): void => {
    event.nativeEvent.stopImmediatePropagation()
  }

  private buildURL = (app: Application, account: Account): string => {
    const { appSuite } = this.state

    if (!account) {
      return appSuite.URL_MAPPING[app]
    }
    const urlBuilder = appSuite.URL_BUILDERS[app]
    if (urlBuilder) {
      return urlBuilder(account)
    }
    return `${appSuite.URL_MAPPING[app]}/${account.pCode}`
  }

  private inferAccountInfoFromURL = (): Partial<Account> | null => {
    const app = this.props.currentApp
    const urlBuilder = this.state.appSuite.URL_BUILDERS[app]

    if (!urlBuilder) {
      return null
    }
    const regex = `^${urlBuilder(ACCOUNT_SNIFFER)}`
    const match = this.window.location.pathname.match(regex)
    if (!match || match.length < 2) {
      return null
    }
    const idOrPCode = match[1].trim()
    const id = parseInt(idOrPCode, 10)
    if (!isNaN(id)) {
      return {
        id
      }
    }
    return {
      pCode: idOrPCode
    }
  }

  private getAccountInfo = (): Partial<Account> | null => {
    const { accountInfo: accountInfoProp } = this.props
    if (accountInfoProp) {
      return accountInfoProp
    }

    const accountInfoUrl: Partial<Account> | null = this.inferAccountInfoFromURL()

    // If the accountInfo prop was passed, try finding an account that matches
    if (accountInfoUrl) {
      return accountInfoUrl
    }

    if (!accountInfoUrl && !accountInfoProp) {
      const accountIdInfoLocalStorage = getAccountIdFromLocalStorage()
      if (accountIdInfoLocalStorage) {
        return {
          id: parseInt(accountIdInfoLocalStorage, 10)
        }
      }
    }
    return null
  }

  private getSelectedAccount = (
    availableAccounts: Account[]
  ): AccountSniffingResult => {
    if (!availableAccounts || availableAccounts.length === 0) {
      return {
        account: null,
        fromURL: false
      }
    }
    const accountInfo: Partial<Account> | null = this.getAccountInfo()

    if (!accountInfo) {
      return {
        account: availableAccounts[0],
        fromURL: false
      }
    }

    // try to see if user can see the url specified account
    const possibleMatch = availableAccounts
      .find(acc => acc.id === accountInfo.id || acc.pCode === accountInfo.pCode)
    if (possibleMatch) {
      return {
        account: possibleMatch,
        fromURL: true
      }
    }
    return {
      account: null,
      fromURL: true
    }
  }

  private setOpenPopup = (type: PopupType | null): void => {
    const popUpContext = { ...this.state.popUpContext }
    popUpContext.openPopup = popUpContext.openPopup !== type ? type : null
    this.setState({ popUpContext })
  }

  private toggleShowAllAccountsModal = (): void => {
    this.setOpenPopup(PopupType.ACCOUNTS_MODAL)
  }

  private getAccountsWithLastAccessed = (accounts) => {
    const lastAccessed = getAllAccountFromLocalStorage()

    return accounts.map((account) => ({ ...account, lastAccessed: lastAccessed[account.id] }))
  }

  private handleAppAccess = (acc: Account) => {
    if (acc == null) return
    this.setState({
      context: {
        ...this.state.context,
        enabledApps: [],
      },
    })
    // fetch account configuration
    this.accountServiceAPI.getAccount(acc.pCode).then(asAcc => {
      const isAllowed = (app: AccountServiceApplication): boolean => {
        return asAcc.config[app] && asAcc.config[app].allowed
      }
      const enabledApps = []
      // For each app check whether they are allowed
      if (isAllowed(AccountServiceApplication.Q_FOR_MARKETERS)) {
        enabledApps.push(AccountServiceApplication.Q_FOR_MARKETERS)
      }
      this.setState({
        context: {
          ...this.state.context,
          enabledApps: enabledApps,
        },
      })
    })
  }

  private handleAccountSelect = (acc: Account) => {
    const { accounts } = this.state
    this.dataService.updateSelectedAccount(acc)
    this.setOpenPopup(null)
    this.setState({
      context: {
        ...this.state.context,
        account: acc
      },
      accounts: this.getAccountsWithLastAccessed(accounts)
    })
  }

  private loadUserInformation = async (): Promise<User> => {
    try {
      const user = await this.dataService.getCurrentUser()
      if (!user) {
        // if we don't have user information - we can't load any other information
        this.setState({ context: DEFAULT_CONTEXT_STATE })
      }
      return user
    } catch (e) {
      console.warn('Can\'t load user information', e)
      this.setState({ context: { ...DEFAULT_CONTEXT_STATE, userInitError: e } })
    }
  }

  private loadAccountsInformation = async (selectedAccount: Partial<Account> | null):
    Promise<{ accounts: Account[], organizations: Organization[] }> => {
      return this.dataService.getAccountsAndOrganizations(selectedAccount).then((accountAndOrganizations) => {
        const accounts: Account[] = accountAndOrganizations.userAccounts
        const organizations: Organization[] = accountAndOrganizations.organizations
        const pcodeOrgMap = organizations.reduce((allMappings, org) => {
          if (org.accounts && org.accounts.length) {
            const { id, name, actions } = org
            org.accounts.forEach(pCode => allMappings[pCode] = { id, name, actions })
          }
          return allMappings
          // tslint:disable-next-line: align
        }, {})
        accounts.forEach(account => account.organization = pcodeOrgMap[account.pCode] || null)
        return { accounts, organizations }
      }).catch((e) => {
        console.warn('Can\'t load accounts information', e)
        this.setState({
          context: {
            ...(this.state.context || DEFAULT_CONTEXT_STATE), // don't erase user if it was loaded successfully
            isLoading: false,
            accountsInitError: e
          }
        })
        return { accounts: [], organizations: [] }
      })
  }
}

export const URL_BUILDERS = {
  ...Q4mSuite.URL_BUILDERS,
  ...GlobalSettingsSuite.URL_BUILDERS,
  ...AuthRoutingSuite.URL_BUILDERS
}

export {
  Application,
  GlobalNavContext,
  GlobalNavContextInfo,
  GlobalNavProps,
  URLBuilders,
}

export default GlobalNav
